import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../../i18n';
import { formatMontant, formatDate, repartirTotalSurLignes, formatPU, getDeviseCourante } from '../../utils/format';
import { Plus, Truck, Phone, MapPin, Mail, Edit, History, ShoppingCart, TrendingDown, Search, X, LayoutGrid, List, Printer } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { Button, Input, Select, Textarea } from '../../components/common/Input';
import PhoneInput from '../../components/common/PhoneInput';
import DateRangePicker from '../../components/common/DateRangePicker';
import { useAuth } from '../../context/AuthContext';
import FormApprovisionnement from './FormApprovisionnement';
import ModalImprimerAppro from './ModalImprimerAppro';
import toast from 'react-hot-toast';

const FormFournisseur = ({ fournisseur, categories, onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    nom: fournisseur?.nom || '',
    telephone: fournisseur?.telephone || '',
    email: fournisseur?.email || '',
    adresse: fournisseur?.adresse || '',
    specialite: fournisseur?.specialite || 'mixte',
    statut: fournisseur?.statut || 'actif',
    notes: fournisseur?.notes || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return toast.error('Nom du fournisseur requis');
    // Email facultatif, mais s'il est rempli, on valide le format
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      return toast.error('Email invalide');
    }
    setLoading(true);
    try {
      if (fournisseur) {
        await api.put(`/fournisseurs/${fournisseur.id}`, form);
      } else {
        await api.post('/fournisseurs', form);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || t('commun.erreurServeur'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Nom du fournisseur *" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Marché Madina Import" />
      <PhoneInput label="Téléphone" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} />
      <Input
        label="Email (pour envoi des bons de commande)"
        type="email"
        value={form.email}
        onChange={e => setForm({ ...form, email: e.target.value })}
        placeholder="email@gmail.com"
      />
      <Textarea label="Adresse" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} placeholder="Quartier, ville..." rows={2} />
      <Select label="Spécialité *" value={form.specialite} onChange={e => setForm({ ...form, specialite: e.target.value })}>
        <option value="mixte">📦 Mixte (toutes catégories)</option>
        {categories.map(c => (
          <option key={c.id} value={c.nom}>{c.nom.charAt(0).toUpperCase() + c.nom.slice(1)}s</option>
        ))}
      </Select>
      {fournisseur && (
        <Select label="Statut" value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
          <option value="actif">Actif</option>
          <option value="inactif">Inactif</option>
        </Select>
      )}
      <Textarea label="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes sur ce fournisseur..." rows={2} />
      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">{t('commun.annuler')}</Button>
        <Button type="submit" loading={loading} className="flex-1">{fournisseur ? t('commun.enregistrer') : t('commun.ajouter')}</Button>
      </div>
    </form>
  );
};

const HistoriqueAppro = ({ fournisseur }) => {
  const { user } = useAuth();
  const peutCorriger = ['proprietaire', 'admin'].includes(user?.role);
  const [appros, setAppros] = useState([]);
  const [apprEdit, setApprEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  // Filtre période — vide = tous. On laisse "tous" par défaut ici car
  // l'historique est typiquement consulté pour voir tout le passé.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin]     = useState('');
  const [apprImpression, setApprImpression] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (dateDebut) params.append('date_debut', dateDebut);
    if (dateFin)   params.append('date_fin',   dateFin);
    const qs = params.toString() ? `?${params}` : '';
    api.get(`/fournisseurs/${fournisseur.id}/approvisionnements${qs}`)
      .then(r => setAppros(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fournisseur.id, dateDebut, dateFin]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div className="space-y-3">
      {/* Filtre période */}
      <DateRangePicker
        debut={dateDebut} fin={dateFin}
        onChange={(d, f) => { setDateDebut(d); setDateFin(f); }}
      />

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : appros.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-8">
          Aucun approvisionnement pour cette période
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-400">{appros.length} approvisionnement(s)</p>
            <p className="text-yellow-400 font-semibold text-sm">
              Total: {formatMontant(appros.reduce((s, a) => s + Number(a.cout_total), 0))}
            </p>
          </div>
          {appros.map(a => (
            <div key={a.id} className="bg-gray-800 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium text-sm">{formatDate(a.date)}</span>
                  {a.bon_commande_numero && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-700/40 text-blue-300"
                          title="Cet approvisionnement a été créé via la réception d'un bon de commande">
                      via {a.bon_commande_numero}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-yellow-400 font-bold">{formatMontant(a.cout_total)}</span>
                  {peutCorriger && (
                    <button
                      onClick={() => setApprEdit(a)}
                      className="p-2 text-gray-400 hover:text-amber-400 hover:bg-gray-700 rounded-lg transition-colors"
                      title="Corriger cet approvisionnement (erreur de prix/quantité)"
                    >
                      <Edit size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => setApprImpression(a)}
                    className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Imprimer / Télécharger PDF / WhatsApp"
                  >
                    <Printer size={14} />
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {repartirTotalSurLignes((a.lignes || []).filter(l => l.variante_id), a.cout_total).map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs text-gray-400">
                    <span>{l.produit_nom} — {l.modele} {l.taille ? `${l.taille}` : ''} {l.couleur}</span>
                    <span>{l.quantite} × {formatPU(l.pu_affiche)} {getDeviseCourante()}</span>
                  </div>
                ))}
              </div>
              {a.notes && <p className="text-gray-600 text-xs mt-2 italic">{a.notes}</p>}
            </div>
          ))}
        </>
      )}

      {/* Modal d'impression */}
      {apprImpression && (
        <ModalImprimerAppro
          appro={apprImpression}
          fournisseur={fournisseur}
          onClose={() => setApprImpression(null)}
        />
      )}

      {/* Modal de correction d'un appro (propriétaire) */}
      <Modal
        open={!!apprEdit}
        onClose={() => setApprEdit(null)}
        title={`Corriger l'approvisionnement — ${fournisseur.nom}`}
        size="lg"
      >
        {apprEdit && (
          <FormApprovisionnement
            fournisseur={fournisseur}
            approEdit={apprEdit}
            onSuccess={() => { setApprEdit(null); charger(); toast.success('Approvisionnement corrigé'); }}
            onCancel={() => setApprEdit(null)}
          />
        )}
      </Modal>
    </div>
  );
};

export default function Fournisseurs() {
  const { canEditCatalog } = useAuth();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalCreer, setModalCreer] = useState(false);
  const [fournisseurModif, setFournisseurModif] = useState(null);
  const [fournisseurHistorique, setFournisseurHistorique] = useState(null);
  const [fournisseurAppro, setFournisseurAppro] = useState(null);
  const [recherche, setRecherche] = useState('');
  // Mode d'affichage : 'cartes' (par défaut) ou 'liste' (vue compacte tableau)
  // Persisté en localStorage pour mémoriser la préférence de l'utilisateur.
  const [vue, setVue] = useState(() => {
    try { return localStorage.getItem('fournisseurs_vue') || 'cartes'; }
    catch { return 'cartes'; }
  });
  useEffect(() => {
    try { localStorage.setItem('fournisseurs_vue', vue); } catch { /* ignore */ }
  }, [vue]);

  const charger = useCallback(async () => {
    try {
      const res = await api.get('/fournisseurs');
      setFournisseurs(res.data);
    } catch { toast.error(t('commun.erreurServeur')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const labelSpecialite = (specialite) => {
    if (specialite === 'mixte') return '📦 Mixte';
    const cat = categories.find(c => c.nom === specialite);
    if (cat) return `${cat.nom.charAt(0).toUpperCase() + cat.nom.slice(1)}s`;
    return specialite;
  };

  const handleSuccess = (msg) => {
    setModalCreer(false); setFournisseurModif(null); setFournisseurAppro(null);
    charger();
    toast.success(msg || t('commun.succes'));
  };

  // Filtre de recherche (nom, téléphone, adresse, spécialité, notes)
  const rechercheNorm = recherche.trim().toLowerCase();
  const fournisseursFiltres = rechercheNorm
    ? fournisseurs.filter(f => {
        const hay = [
          f.nom,
          f.telephone,
          f.adresse,
          f.specialite,
          labelSpecialite(f.specialite),
          f.notes,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(rechercheNorm);
      })
    : fournisseurs;

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('fournisseurs.titre')}</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {rechercheNorm
              ? `${fournisseursFiltres.length} / ${fournisseurs.length} fournisseur(s)`
              : `${fournisseurs.length} fournisseur(s)`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sélecteur de vue : cartes ↔ liste */}
          <div className="inline-flex bg-gray-800 border border-gray-700 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setVue('cartes')}
              title="Vue cartes"
              className={`p-1.5 rounded-md transition-colors ${
                vue === 'cartes'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              onClick={() => setVue('liste')}
              title="Vue liste compacte"
              className={`p-1.5 rounded-md transition-colors ${
                vue === 'liste'
                  ? 'bg-yellow-500 text-black'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <List size={15} />
            </button>
          </div>
          {canEditCatalog && (
            <Button onClick={() => setModalCreer(true)}>
              <Plus size={16} /><span>{t('fournisseurs.ajouterFournisseur')}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Champ de recherche */}
      {fournisseurs.length > 0 && (
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            type="text"
            value={recherche}
            onChange={e => setRecherche(e.target.value)}
            placeholder="Rechercher un fournisseur (nom, téléphone, adresse, spécialité...)"
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-9 py-2.5 focus:outline-none focus:border-yellow-500 transition-colors placeholder-gray-600 text-sm"
          />
          {recherche && (
            <button
              type="button"
              onClick={() => setRecherche('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white rounded transition-colors"
              title="Effacer la recherche"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Résumé global */}
      {fournisseurs.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Total fournisseurs', value: fournisseurs.length },
            { label: 'Actifs', value: fournisseurs.filter(f => f.statut === 'actif').length },
            { label: 'Mixte (tout)', value: fournisseurs.filter(f => f.specialite === 'mixte').length },
            { label: 'Total achats', value: formatMontant(fournisseurs.reduce((s, f) => s + Number(f.total_achats || 0), 0)) },
          ].map(({ label, value }) => (
            <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-xs">{label}</p>
              <p className="text-white font-bold text-lg mt-1">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Liste fournisseurs */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </div>
      ) : fournisseurs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 space-y-3">
          <Truck size={40} className="text-gray-600" />
          <p className="text-gray-500">Aucun fournisseur enregistré</p>
          <Button onClick={() => setModalCreer(true)}><Plus size={16} /><span>Ajouter un fournisseur</span></Button>
        </div>
      ) : fournisseursFiltres.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 space-y-2">
          <Search size={28} className="text-gray-600" />
          <p className="text-gray-500 text-sm">Aucun fournisseur ne correspond à « {recherche} »</p>
          <button onClick={() => setRecherche('')} className="text-yellow-400 hover:text-yellow-300 text-xs underline">
            Effacer la recherche
          </button>
        </div>
      ) : vue === 'liste' ? (
        /* ── Vue LISTE compacte (tableau dense) ─────────────────────────── */
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-auto max-h-[70vh]">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-900 sticky top-0 z-10">
              <tr className="text-xs text-gray-400 uppercase tracking-wide">
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Spécialité</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">Téléphone</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Adresse</th>
                <th className="text-center px-4 py-3">Statut</th>
                <th className="text-right px-4 py-3">Total achats</th>
                <th className="text-center px-4 py-3 hidden sm:table-cell">Cmds</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {fournisseursFiltres.map(f => (
                <tr key={f.id} className={`hover:bg-gray-800/40 ${f.statut === 'inactif' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3 font-medium text-white">{f.nom}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <Badge variant="yellow">{labelSpecialite(f.specialite)}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-300 hidden sm:table-cell">{f.telephone || <span className="text-gray-600">—</span>}</td>
                  <td className="px-4 py-3 text-gray-400 hidden lg:table-cell">
                    {f.adresse ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> <span className="truncate max-w-[180px]">{f.adresse}</span>
                      </span>
                    ) : <span className="text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={f.statut === 'actif' ? 'green' : 'gray'}>{f.statut}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-yellow-400 font-semibold">{formatMontant(f.total_achats || 0)}</td>
                  <td className="px-4 py-3 text-center text-gray-400 hidden sm:table-cell">{f.nb_approvisionnements || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setFournisseurHistorique(f)}
                        title="Historique"
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                      >
                        <History size={13} />
                      </button>
                      <button
                        onClick={() => setFournisseurModif(f)}
                        title="Modifier"
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-md transition-colors"
                      >
                        <Edit size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ── Vue CARTES (par défaut) ──────────────────────────────────────── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {fournisseursFiltres.map(f => (
            <div key={f.id} className={`bg-gray-900 border rounded-xl p-5 transition-all hover:border-yellow-600/40 ${f.statut === 'inactif' ? 'border-gray-800 opacity-60' : 'border-gray-800'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold">{f.nom}</h3>
                    <Badge variant={f.statut === 'actif' ? 'green' : 'gray'}>{f.statut}</Badge>
                    <Badge variant="yellow">{labelSpecialite(f.specialite)}</Badge>
                  </div>
                  <div className="mt-2 space-y-1">
                    {f.telephone && (
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Phone size={13} /><span>{f.telephone}</span>
                      </div>
                    )}
                    {f.email && (
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Mail size={13} /><span className="truncate">{f.email}</span>
                      </div>
                    )}
                    {f.adresse && (
                      <div className="flex items-center space-x-2 text-gray-400 text-sm">
                        <MapPin size={13} /><span className="truncate">{f.adresse}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-yellow-400 font-bold">{formatMontant(f.total_achats || 0)}</p>
                  <p className="text-gray-500 text-xs">{f.nb_approvisionnements || 0} commande(s)</p>
                </div>
              </div>

              {f.notes && <p className="text-gray-600 text-xs mt-3 italic border-t border-gray-800 pt-3">{f.notes}</p>}

              {/* Actions — l'approvisionnement direct se fait désormais
                  depuis la page Commandes (sidebar) pour rester cohérent
                  avec le flux bon de commande → réception. */}
              <div className="flex space-x-2 mt-4 pt-3 border-t border-gray-800">
                <button
                  onClick={() => setFournisseurHistorique(f)}
                  className="flex-1 flex items-center justify-center space-x-1.5 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs transition-colors"
                >
                  <History size={13} /><span>Historique</span>
                </button>
                <button
                  onClick={() => setFournisseurModif(f)}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg text-xs transition-colors"
                >
                  <Edit size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <Modal open={modalCreer} onClose={() => setModalCreer(false)} title={t('fournisseurs.ajouterFournisseur')} size="sm">
        <FormFournisseur categories={categories} onSuccess={() => handleSuccess('Fournisseur ajouté')} onCancel={() => setModalCreer(false)} />
      </Modal>

      <Modal open={!!fournisseurModif} onClose={() => setFournisseurModif(null)} title={`Modifier — ${fournisseurModif?.nom}`} size="sm">
        {fournisseurModif && <FormFournisseur fournisseur={fournisseurModif} categories={categories} onSuccess={() => handleSuccess('Fournisseur modifié')} onCancel={() => setFournisseurModif(null)} />}
      </Modal>

      <Modal open={!!fournisseurHistorique} onClose={() => setFournisseurHistorique(null)} title={`Historique — ${fournisseurHistorique?.nom}`} size="md">
        {fournisseurHistorique && <HistoriqueAppro fournisseur={fournisseurHistorique} />}
      </Modal>

      <Modal open={!!fournisseurAppro} onClose={() => setFournisseurAppro(null)} title={`Approvisionnement — ${fournisseurAppro?.nom}`} size="lg">
        {fournisseurAppro && (
          <FormApprovisionnement
            fournisseur={fournisseurAppro}
            onSuccess={() => handleSuccess('Approvisionnement enregistré')}
            onCancel={() => setFournisseurAppro(null)}
          />
        )}
      </Modal>
    </div>
  );
}
